import { useSelector } from 'react-redux';
import { useGetOrganizationQuery } from 'uf-api-rtk/store/Api';
import { getActiveOrganizationKey } from 'uf/app/selectors';

interface OrgHelpURLResult {
  data?: string;
  isLoading: boolean;
  isError: boolean;
}

const useOrgHelpURL = (): OrgHelpURLResult => {
  const orgKey = useSelector(getActiveOrganizationKey);
  const {
    data: orgData,
    isLoading,
    isError,
  } = useGetOrganizationQuery({ key: orgKey }, { skip: !orgKey });
  return {
    data: orgData?.help_url,
    isLoading,
    isError,
  };
};

/**
 * This is restricted to the hook, for now, as the ModuleKey type defined by
 * the API schema is string. If we want to cover the entirety of the app
 * it will require a BE schema change and some code changes to to fix TS errors.
 */
type ModuleKey =
  | 'household_cost'
  | 'tnc_conservation'
  | 'energy_demand'
  | 'water_demand'
  | 'ghg_emission'
  | 'resiliency'
  | 'transit_accessibility'
  | 'transportation_anywhere'
  | 'walk_accessibility'
  | 'land_consumption';

export const moduleMethodologyUrls: Record<ModuleKey, string> = {
  household_cost:
    'https://urbanfootprint.us.document360.io/docs/household-costs-analysis',
  tnc_conservation:
    'https://docs.urbanfootprint.com/assets/UF-Conservation-Module-Methodology.pdf',
  energy_demand:
    'https://urbanfootprint.us.document360.io/docs/energy-use-analysis',
  water_demand:
    'https://urbanfootprint.us.document360.io/docs/water-use-analysis',
  ghg_emission:
    'https://urbanfootprint.us.document360.io/docs/emissions-analysis',
  resiliency:
    'https://urbanfootprint.us.document360.io/docs/risk-and-resilience-analysis',
  transit_accessibility:
    'https://urbanfootprint.us.document360.io/docs/walk-and-transit-accessibility-analysis',
  transportation_anywhere:
    'https://urbanfootprint.us.document360.io/docs/transportation-analysis',
  walk_accessibility:
    'https://urbanfootprint.us.document360.io/docs/walk-and-transit-accessibility-analysis',
  land_consumption:
    'https://urbanfootprint.us.document360.io/docs/land-consumption-analysis',
};

interface OrgMethodologyLinks {
  [key: string]: string | undefined;
}

const useOrgMethodologyLinks = (): OrgMethodologyLinks => moduleMethodologyUrls;

export { useOrgHelpURL, useOrgMethodologyLinks };
